/**
 * Helpers to resolve which organization a white-label host belongs to
 * (e.g. portal.amsitel.com -> organizations.domain = 'amsitel.com').
 */

// Suffixes that can never identify an organization on their own.
const PUBLIC_SUFFIXES = new Set([
  'com', 'mx', 'org', 'net', 'edu', 'gob', 'gov', 'io', 'co', 'app', 'dev',
  'com.mx', 'org.mx', 'gob.mx', 'net.mx', 'edu.mx',
  // Shared hosting platforms: a sibling project must not match another tenant.
  'vercel.app', 'netlify.app', 'pages.dev', 'github.io',
]);

const IPV4 = /^\d{1,3}(\.\d{1,3}){3}$/;

/**
 * Returns the domains to look up for a hostname, most specific first:
 * the full host (without `www.`) followed by each parent domain, stopping
 * before a bare public suffix. Local hosts and IPs return an empty list.
 */
export function candidateDomains(hostname: string | null | undefined): string[] {
  const host = (hostname || '').trim().toLowerCase().replace(/\.$/, '').replace(/^www\./, '');
  if (!host || !host.includes('.') || IPV4.test(host) || host.includes(':')) return [];
  if (host === 'localhost' || host.endsWith('.localhost') || host.endsWith('.local')) return [];

  const labels = host.split('.');
  const candidates: string[] = [];
  for (let i = 0; i < labels.length - 1; i += 1) {
    const candidate = labels.slice(i).join('.');
    if (PUBLIC_SUFFIXES.has(candidate)) break;
    candidates.push(candidate);
  }
  return candidates;
}

/** Picks the row whose `domain` is the most specific candidate. */
export function pickMostSpecific<T extends { domain: string | null }>(rows: T[] | null | undefined, candidates: string[]): T | null {
  if (!rows?.length) return null;
  let best: T | null = null;
  let bestIndex = Infinity;
  for (const row of rows) {
    const index = candidates.indexOf((row.domain || '').toLowerCase());
    if (index !== -1 && index < bestIndex) {
      best = row;
      bestIndex = index;
    }
  }
  return best;
}

/** Normalizes a brand color to `#rrggbb`, or null when it is not a valid hex. */
export function normalizeHexColor(value: string | null | undefined): string | null {
  const raw = (value || '').trim().replace(/^#/, '');
  if (/^[0-9a-f]{6}$/i.test(raw)) return `#${raw.toLowerCase()}`;
  if (/^[0-9a-f]{3}$/i.test(raw)) return `#${raw.split('').map((c) => c + c).join('').toLowerCase()}`;
  return null;
}
